import "./createGroupsMenu.css";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { colors } from "utils";
import { primaryButtonStyles, PrimaryButtonLabel } from "./PrimaryButton";
import { useMenuButton } from "./useMenuButton";

// "Create Groups" menu button (Figma 6452:83778): Button/Primary 195x48 with a
// chevron, and its 195x120 "Menu/Sort" menu (6452:83780) one pixel below.

const Wrapper = styled.div`
  display: flex;
  position: relative;
`;

const Button = styled.button`
  ${primaryButtonStyles}
  display: inline-flex;
  gap: 0;
  padding: 0 8px 0 16px;
  width: 195px;
`;

// Grows so the chevron sits 8px from the right edge whatever the text metrics.
const Label = styled(PrimaryButtonLabel)`
  flex: 1 1 auto;
  text-align: left;
`;

const Chevron = styled.svg`
  flex-shrink: 0;
`;

// The 1px border is Figma's inside stroke: drawn over the rows, so each row
// keeps the menu's full 195px width.
const Menu = styled.div`
  background: #ffffff;
  border-radius: 6px;
  box-shadow: none;
  box-sizing: border-box;
  height: 120px;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: calc(100% + 1px);
  width: 195px;
  z-index: 200;

  &::after {
    border: 1px solid ${colors.blue300};
    border-radius: 6px;
    bottom: 0;
    content: "";
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

// Keyboard focus draws an inset bar (the dropdowns' convention) that the
// menu's overflow cannot clip; a hovered row keeps Figma's highlight alone.
const keyboardFocusBar = css`
  box-shadow: inset 3px 0 0 ${colors.blue500};
`;

const MenuItem = styled.button`
  background: ${(p) => (p.$active ? colors.blue50 : "#ffffff")};
  border: none;
  border-radius: 0;
  box-sizing: border-box;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: block;
  font-family: "Create Groups Menu Roboto", Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 40px;
  letter-spacing: 0;
  line-height: 24px;
  margin: 0;
  padding: 8px 16px;
  text-align: left;
  white-space: nowrap;
  width: 100%;

  &:focus,
  &:focus-visible {
    outline: none;
  }
  ${(p) => p.$keyboardFocus && keyboardFocusBar}
`;

const ChevronDownIcon = () => (
  <Chevron width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke={colors.gray50} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Chevron>
);

export const CreateGroupsMenuButton = ({
  onOpen,
  onCreateLessonGroups,
  onCreateQuickGroup,
  onCreateWarmUpGroups,
  // Moves focus to the button once, e.g. after the last warm-up card is archived.
  focusRequest = false,
  onFocusRequestDone,
}) => {
  const items = [
    { label: "Lesson Groups", action: onCreateLessonGroups },
    { label: "Quick Group", action: onCreateQuickGroup },
    { label: "Warm-Up Groups", action: onCreateWarmUpGroups },
  ];
  const menu = useMenuButton({ idPrefix: "create-groups-menu", itemCount: items.length, onOpen });
  const { buttonRef } = menu;

  useEffect(() => {
    if (!focusRequest) return;
    buttonRef.current?.focus();
    onFocusRequestDone?.();
  }, [focusRequest, onFocusRequestDone, buttonRef]);

  return (
    <Wrapper {...menu.wrapperProps}>
      <Button {...menu.buttonProps}>
        <Label>Create Groups</Label>
        <ChevronDownIcon />
      </Button>
      {menu.isOpen && (
        <Menu {...menu.menuProps} aria-label="Create Groups">
          {items.map(({ label, action }, index) => (
            <MenuItem key={label} {...menu.itemProps(index, action)}>
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};
